import { el, clear, showToast, showConfirm, unitLabel, t } from './dom';
import { api, ApiError } from './api';
import type { User } from './types';
import { buildLanguageSelector } from './languageSelector';
import { COMPANY } from './config/company';

interface AdminProduct {
  id: string;
  sku: string;
  name: string;
  unit: string;
  referencePrice: number;
  isActive: boolean;
  category: { id: string; name: string };
}

interface AdminCategory {
  id: string;
  name: string;
  displayOrder: number;
}

interface ImportPreviewItem {
  sku: string;
  name: string;
  unit: string;
  unitCents: number;
  category: string;
  ean: string | null;
  status: 'NEW' | 'UPDATE' | 'UNCHANGED';
  existingProductId: string | null;
  existingPriceCents: number | null;
}

export async function renderProductCatalog(
  root: HTMLElement,
  user: User,
  onBack: () => void,
) {
  clear(root);

  const header = el('header', { className: 'header' });

  const brand = el('div', { className: 'header-brand' });
  const logoImg = document.createElement('img');
  logoImg.src = COMPANY.logoUrl;
  logoImg.alt = COMPANY.name;
  logoImg.className = 'header-logo';
  brand.appendChild(logoImg);
  brand.appendChild(el('h1', { className: 'header-title', text: t('catalog.title') }));

  header.append(
    el('button', {
      className: 'btn-logout',
      text: t('common.back'),
      onClick: onBack,
    }),
    brand,
    el('div', { className: 'user-info' }, [
      buildLanguageSelector(() => renderProductCatalog(root, user, onBack)),
    ]),
  );

  const main = el('main', { className: 'main' });
  const content = el('div');
  main.append(content);
  root.append(header, main);

  let categories: AdminCategory[] = [];
  let products: AdminProduct[] = [];

  const loadAll = async () => {
    clear(content);
    content.appendChild(el('p', { text: t('common.loading'), className: 'loading' }));
    try {
      const data = await api.request<{ categories: AdminCategory[]; products: AdminProduct[] }>(
        'GET',
        '/admin/products?includeInactive=true',
      );
      categories = data.categories;
      products = data.products;
      render();
    } catch (e) {
      clear(content);
      const msg = e instanceof ApiError ? e.message : t('common.networkError');
      content.appendChild(el('div', { className: 'empty-state', text: msg }));
    }
  };

  const render = () => {
    clear(content);

    // Toolbar
    const toolbar = el('div', { className: 'toolbar' });

    const importBtn = el('button', {
      className: 'btn btn-primary',
      text: t('catalog.import'),
      onClick: () => openImportModal(),
    });

    const newBtn = el('button', {
      className: 'btn btn-success',
      text: t('catalog.newProduct'),
      onClick: () => openProductForm(null),
    });

    toolbar.append(importBtn, newBtn);
    content.appendChild(toolbar);

    // Stats
    const statsCard = el('div', { className: 'stats-card' });
    const active = products.filter(p => p.isActive).length;
    const inactive = products.length - active;
    statsCard.appendChild(el('div', {
      text: t('catalog.stats', {
        total: products.length,
        active,
        inactive,
        cats: categories.length,
      }),
    }));
    content.appendChild(statsCard);

    if (products.length === 0) {
      content.appendChild(el('div', {
        className: 'empty-state',
        text: t('catalog.empty'),
      }));
      return;
    }

    // Agrupar por categoría
    const byCategory = new Map<string, AdminProduct[]>();
    for (const p of products) {
      const arr = byCategory.get(p.category.name) ?? [];
      arr.push(p);
      byCategory.set(p.category.name, arr);
    }

    const sortedCats = [...byCategory.keys()].sort((a, b) => {
      const ca = categories.find(c => c.name === a)?.displayOrder ?? 999;
      const cb = categories.find(c => c.name === b)?.displayOrder ?? 999;
      return ca - cb || a.localeCompare(b);
    });

    for (const catName of sortedCats) {
      const catCard = el('div', { className: 'card' });
      catCard.appendChild(el('div', { className: 'card-title', text: catName }));

      const table = el('table', { className: 'items-table' });
      const thead = el('thead');
      const headRow = el('tr');
      [
        t('catalog.col.sku'),
        t('catalog.col.product'),
        t('catalog.col.unit'),
        t('catalog.col.price'),
        t('catalog.col.status'),
        '',
      ].forEach(text => {
        headRow.appendChild(el('th', { text }));
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = el('tbody');
      const items = byCategory.get(catName)!;
      items.sort((a, b) => a.name.localeCompare(b.name));

      for (const p of items) {
        const tr = el('tr');
        if (!p.isActive) tr.style.opacity = '0.5';
        tr.append(
          el('td', { text: p.sku }),
          el('td', { text: p.name }),
          el('td', { text: unitLabel(p.unit) }),
          el('td', {
            text: (p.referencePrice / 100).toFixed(2).replace('.', ','),
            className: 'numeric',
          }),
          el('td', {
            text: p.isActive ? t('catalog.statusActive') : t('catalog.statusInactive'),
          }),
        );
        const actions = el('td');
        actions.appendChild(el('button', {
          className: 'btn btn-secondary',
          text: t('common.edit'),
          onClick: () => openProductForm(p),
        }));
        if (p.isActive) {
          actions.appendChild(el('button', {
            className: 'btn btn-danger',
            text: t('catalog.deactivate'),
            onClick: async () => {
              if (!await showConfirm(t('catalog.deactivateConfirm', { name: p.name }))) return;
              try {
                await api.request('DELETE', `/admin/products/${p.id}`);
                showToast(t('catalog.deactivated'), 'success');
                await loadAll();
              } catch (e) {
                const msg = e instanceof ApiError ? e.message : t('common.unknownError');
                showToast(msg, 'error');
              }
            },
          }));
        }
        tr.appendChild(actions);
        tbody.appendChild(tr);
      }

      table.appendChild(tbody);
      catCard.appendChild(table);
      content.appendChild(catCard);
    }
  };

  // ========== Modal: form de producto ==========
  const openProductForm = (existing: AdminProduct | null) => {
    const backdrop = el('div', { className: 'modal-backdrop' });
    const modal = el('div', { className: 'modal' });
    modal.appendChild(el('h3', { text: existing ? t('product.formTitleEdit') : t('product.formTitleNew') }));

    const skuGroup = el('div', { className: 'form-group' });
    skuGroup.appendChild(el('label', { text: t('product.sku') }));
    const skuInput = document.createElement('input');
    skuInput.type = 'text';
    skuInput.maxLength = 50;
    skuInput.value = existing?.sku ?? '';
    if (existing) skuInput.disabled = true;
    skuGroup.appendChild(skuInput);

    const nameGroup = el('div', { className: 'form-group' });
    nameGroup.appendChild(el('label', { text: t('product.name') }));
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.maxLength = 200;
    nameInput.value = existing?.name ?? '';
    nameGroup.appendChild(nameInput);

    const unitGroup = el('div', { className: 'form-group' });
    unitGroup.appendChild(el('label', { text: t('product.unit') }));
    const unitSelect = document.createElement('select');
    ['kg', 'unidad', 'litro', 'gr', 'paquete'].forEach(u => {
      const opt = document.createElement('option');
      opt.value = u;
      opt.textContent = unitLabel(u);
      if (existing?.unit === u) opt.selected = true;
      unitSelect.appendChild(opt);
    });
    unitGroup.appendChild(unitSelect);

    const priceGroup = el('div', { className: 'form-group' });
    priceGroup.appendChild(el('label', { text: t('product.priceEur') }));
    const priceInput = document.createElement('input');
    priceInput.type = 'text';
    priceInput.value = existing ? (existing.referencePrice / 100).toFixed(2).replace('.', ',') : '';
    priceGroup.appendChild(priceInput);

    const catGroup = el('div', { className: 'form-group' });
    catGroup.appendChild(el('label', { text: t('product.category') }));
    const catInput = document.createElement('input');
    catInput.type = 'text';
    catInput.maxLength = 100;
    catInput.value = existing?.category.name ?? '';
    catInput.setAttribute('list', 'category-options');
    catGroup.appendChild(catInput);
    const datalist = document.createElement('datalist');
    datalist.id = 'category-options';
    categories.forEach(c => {
      const opt = document.createElement('option');
      opt.value = c.name;
      datalist.appendChild(opt);
    });
    catGroup.appendChild(datalist);

    const actions = el('div', { className: 'modal-actions' });
    const cancelBtn = el('button', {
      className: 'btn btn-secondary',
      text: t('common.cancel'),
      onClick: () => backdrop.remove(),
    });
    const saveBtn = el('button', {
      className: 'btn btn-primary',
      text: t('common.save'),
      onClick: async () => {
        // Validacion de precio (numero positivo)
        const priceStr = priceInput.value.trim().replace(',', '.');
        const priceFloat = parseFloat(priceStr);
        if (!Number.isFinite(priceFloat) || priceFloat < 0) {
          showToast(t('product.invalidPrice'), 'error');
          priceInput.focus();
          return;
        }
        const priceCents = Math.round(priceFloat * 100);

        // Validacion de campos obligatorios
        // - SKU: solo al crear (al editar el input esta disabled)
        // - Nombre y categoria: siempre
        const sku = skuInput.value.trim();
        if (!existing && !sku) {
          showToast('SKU es obligatorio', 'error');
          skuInput.focus();
          return;
        }
        const name = nameInput.value.trim();
        if (!name) {
          showToast('El nombre es obligatorio', 'error');
          nameInput.focus();
          return;
        }
        const categoryName = catInput.value.trim();
        if (!categoryName) {
          showToast('La categoría es obligatoria', 'error');
          catInput.focus();
          return;
        }

        try {
          if (existing) {
            await api.request('PATCH', `/admin/products/${existing.id}`, {
              name,
              unit: unitSelect.value,
              referencePrice: priceCents,
              categoryName,
            });
            showToast(t('product.updated'), 'success');
          } else {
            await api.request('POST', '/admin/products', {
              sku,
              name,
              unit: unitSelect.value,
              referencePrice: priceCents,
              categoryName,
            });
            showToast(t('product.created'), 'success');
          }
          backdrop.remove();
          await loadAll();
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : t('common.unknownError');
          showToast(msg, 'error');
        }
      },
    });
    actions.append(cancelBtn, saveBtn);

    modal.append(skuGroup, nameGroup, unitGroup, priceGroup, catGroup, actions);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
  };

  // ========== Modal: importar factura ==========
  const openImportModal = () => {
    const backdrop = el('div', { className: 'modal-backdrop' });
    const modal = el('div', { className: 'modal' });
    modal.style.maxWidth = '900px';
    modal.appendChild(el('h3', { text: t('import.title') }));
    modal.appendChild(el('p', { text: t('import.description') }));

    const fileGroup = el('div', { className: 'form-group' });
    fileGroup.appendChild(el('label', { text: t('import.fileLabel') }));
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'application/pdf,.pdf';
    fileGroup.appendChild(fileInput);
    modal.appendChild(fileGroup);

    const previewContainer = el('div');
    modal.appendChild(previewContainer);

    let currentToken: string | null = null;

    const actions = el('div', { className: 'modal-actions' });
    const cancelBtn = el('button', {
      className: 'btn btn-secondary',
      text: t('import.close'),
      onClick: () => backdrop.remove(),
    });

    const previewBtn = el('button', {
      className: 'btn btn-primary',
      text: t('import.preview'),
      onClick: async () => {
        if (!fileInput.files || fileInput.files.length === 0) {
          showToast(t('import.choosePdf'), 'error');
          return;
        }
        const file = fileInput.files[0];
        if (file.type !== 'application/pdf') {
          showToast(t('import.onlyPdf'), 'error');
          return;
        }

        previewBtn.disabled = true;
        previewBtn.textContent = t('import.processing');
        clear(previewContainer);
        previewContainer.appendChild(el('p', { text: t('import.reading'), className: 'loading' }));

        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/v1/admin/products/import-invoice/preview`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${(api as any).accessToken}`,
              'X-Device-Id': api.getDeviceId(),
            },
            body: formData,
          });

          const json = await response.json();
          if (!json.success) {
            throw new Error(json.error?.message ?? t('common.unknownError'));
          }

          currentToken = json.data.token;
          renderPreview(json.data.preview);
          confirmBtn.style.display = 'inline-block';
        } catch (e: any) {
          clear(previewContainer);
          previewContainer.appendChild(el('div', {
            className: 'empty-state',
            text: e?.message ?? t('common.unknownError'),
          }));
        } finally {
          previewBtn.disabled = false;
          previewBtn.textContent = t('import.preview');
        }
      },
    });

    const confirmBtn = el('button', {
      className: 'btn btn-success',
      text: t('import.confirm'),
      onClick: async () => {
        if (!currentToken) return;
        if (!await showConfirm(t('import.confirmDialog'))) return;
        confirmBtn.disabled = true;
        try {
          const result = await api.request<{ created: number; updated: number; skipped: number }>(
            'POST',
            '/admin/products/import-invoice/confirm',
            { token: currentToken },
          );
          showToast(
            t('import.result', { created: result.created, updated: result.updated, skipped: result.skipped }),
            'success',
          );
          backdrop.remove();
          await loadAll();
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : t('common.unknownError');
          showToast(msg, 'error');
          confirmBtn.disabled = false;
        }
      },
    });
    confirmBtn.style.display = 'none';

    actions.append(cancelBtn, previewBtn, confirmBtn);
    modal.appendChild(actions);

    const renderPreview = (preview: {
      invoiceNumber: string | null;
      invoiceDate: string | null;
      totalCents: number | null;
      items: ImportPreviewItem[];
      warnings: string[];
    }) => {
      clear(previewContainer);

      const info = el('div', { className: 'card' });
      info.appendChild(el('div', {
        text: t('import.invoiceInfo', {
          number: preview.invoiceNumber ?? '—',
          date: preview.invoiceDate ?? '—',
          total: preview.totalCents ? (preview.totalCents / 100).toFixed(2) : '—',
        }),
      }));
      previewContainer.appendChild(info);

      const newCount = preview.items.filter(i => i.status === 'NEW').length;
      const updateCount = preview.items.filter(i => i.status === 'UPDATE').length;
      const sameCount = preview.items.filter(i => i.status === 'UNCHANGED').length;

      const summary = el('div', { className: 'card' });
      summary.appendChild(el('div', {
        text: t('import.summary', {
          total: preview.items.length,
          new: newCount,
          update: updateCount,
          same: sameCount,
        }),
      }));
      previewContainer.appendChild(summary);

      const table = el('table', { className: 'items-table' });
      const thead = el('thead');
      const hr = el('tr');
      [
        t('catalog.col.status'),
        t('catalog.col.sku'),
        t('catalog.col.product'),
        t('catalog.col.unit'),
        t('catalog.col.price'),
        t('catalog.col.category'),
      ].forEach(text => {
        hr.appendChild(el('th', { text }));
      });
      thead.appendChild(hr);
      table.appendChild(thead);

      const tbody = el('tbody');
      for (const item of preview.items) {
        const tr = el('tr');
        let statusText = '';
        let bgColor = '';
        if (item.status === 'NEW') {
          statusText = t('import.statusNew');
          bgColor = '#d4edda';
        } else if (item.status === 'UPDATE') {
          statusText = t('import.statusUpdate');
          bgColor = '#fff3cd';
        } else {
          statusText = t('import.statusUnchanged');
          bgColor = '#f8f9fa';
        }
        tr.style.backgroundColor = bgColor;

        const priceText = (item.unitCents / 100).toFixed(2).replace('.', ',');
        let priceCell: string;
        if (item.status === 'UPDATE' && item.existingPriceCents !== null) {
          const oldPrice = (item.existingPriceCents / 100).toFixed(2).replace('.', ',');
          priceCell = `${oldPrice} → ${priceText}`;
        } else {
          priceCell = priceText;
        }

        tr.append(
          el('td', { text: statusText }),
          el('td', { text: item.sku }),
          el('td', { text: item.name }),
          el('td', { text: unitLabel(item.unit) }),
          el('td', { text: priceCell, className: 'numeric' }),
          el('td', { text: item.category }),
        );
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);

      const tableWrapper = el('div', { className: 'card' });
      tableWrapper.style.maxHeight = '400px';
      tableWrapper.style.overflow = 'auto';
      tableWrapper.appendChild(table);
      previewContainer.appendChild(tableWrapper);

      if (preview.warnings.length > 0) {
        const warns = el('div', { className: 'card' });
        warns.appendChild(el('div', { text: t('import.warnings') }));
        const ul = el('ul');
        preview.warnings.forEach(w => ul.appendChild(el('li', { text: w })));
        warns.appendChild(ul);
        previewContainer.appendChild(warns);
      }
    };

    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
  };

  await loadAll();
}
